import clsx from "clsx";
import { A } from "components/A";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { ScrollArea } from "components/ScrollArea";
import { TagComponent } from "components/TagComponent";
import { getSimilarPosts, posts } from "data/posts";
import { Post, PostImage } from "data/types";
import { formatInTimeZone } from "date-fns-tz";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { generateNextSeo } from "next-seo/pages";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { BlurhashCanvas } from "react-blurhash";
import {
  MdAutoFixHigh,
  MdDownload,
  MdEdit,
  MdErrorOutline,
} from "react-icons/md";
import { BlogPosting, WithContext } from "schema-dts";
import { linkify } from "utils/linkify";
import { useOrigin } from "../../hooks";
import { generatePalette } from "styles/colors";

const betterEncodeURI = (str: string) => {
  return encodeURI(str).replace("'", "%27");
};

const FullscreenImage = dynamic(() => import("components/FullscreenImage"), {
  ssr: false,
});

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: posts.flatMap((p) => [
      { params: { slug: [p.id.toString(), p.slug] } },
      ...p.images.map((_, index) => ({
        params: { slug: [p.id.toString(), p.slug, (index + 1).toString()] },
      })),
    ]),
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const query = context.params?.slug;
  const post = query
    ? posts.find((p) => p.id === Number(query[0]) && p.slug === query[1])
    : undefined;

  const currentIndex = +(query?.[2] || 1) - 1;
  const currentImage = post?.images[currentIndex];
  const style = currentImage && generatePalette(currentImage.hue);

  return {
    props: {
      post,
      currentImage,
      currentIndex,
      similarPosts: post && getSimilarPosts(post),
      style,
    },
    notFound: !post || !currentImage,
  };
};

interface ImagePageProps {
  post: Post;
  currentImage: PostImage;
  currentIndex: number;
  similarPosts: Post[];
  style: string;
}

const ImagePage: NextPage<ImagePageProps> = ({
  post,
  currentImage,
  currentIndex,
  similarPosts,
  style,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const basePath =
    "/images/" + [router.query.slug].flat()?.slice(0, 2).join("/");

  const origin = useOrigin();

  const title = clsx(
    post.title,
    post.images.length > 1 && `| ${post.images.length} versions`,
  );

  const jsonLd: WithContext<BlogPosting> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    url: `${origin}/images/${post.id}/${post.slug}`,
    headline: title,
    description: post.description || "No description",
    author: {
      "@type": "Person",
      name: "irisarco",
      url: "https://irisarco.art",
    },
    datePublished: post.createdAt,
    dateModified: post.updatedAt,
    image: post.images.map((i) => betterEncodeURI(i.urls.share)),
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoaded(false);
  }, [post.id]);

  return (
    <article className="relative container mb-8 md:mb-0">
      <Head>
        {generateNextSeo({
          title,
          description: post.description || undefined,
          canonical: `${origin}/images/${post.id}/${post.slug}`,
          openGraph: {
            images: [{ url: betterEncodeURI(currentImage.urls.share) }],
          },
          twitter: { cardType: "summary_large_image" },
        })}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <style>{style}</style>
      </Head>

      <BlurhashCanvas
        hash={currentImage.blurhash}
        className="absolute inset-0 -z-10 h-full w-full opacity-50 blur-3xl dark:opacity-30"
      />

      <div className="flex flex-col items-center gap-4 md:flex-row md:items-start md:gap-10">
        <figure
          style={{
            aspectRatio: `${currentImage.width} / ${currentImage.height}`,
          }}
          className="relative max-h-[calc(100vh-4rem)] w-full flex-1"
        >
          {!loaded && (
            <BlurhashCanvas
              hash={currentImage.blurhash}
              height={128}
              width={Math.round(
                128 * (currentImage.width / currentImage.height),
              )}
              className="h-full w-full object-contain opacity-50"
            />
          )}
          {currentImage.urls.medium.endsWith(".mp4") ? (
            <video
              className="absolute inset-0 h-full w-full object-contain"
              src={currentImage.urls.medium}
              loop
              autoPlay
              muted
            />
          ) : (
            <Image
              key={post.id}
              className="cursor-pointer object-contain"
              src={currentImage.urls.medium}
              alt={`${post.title} (${currentIndex + 1}/${post.images.length})`}
              fill
              priority
              onLoad={() => setLoaded(true)}
              onClick={() => setOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              fetchPriority="high"
            />
          )}
        </figure>

        <Card className="relative mb-4 flex w-full max-w-sm flex-col overflow-hidden rounded-md md:mb-0 md:max-h-[calc(100vh-4rem)] md:w-[330px]">
          {process.env.NODE_ENV === "development" && (
            <Link
              href={`/images/${post.id}/edit`}
              className="absolute top-3 right-3"
              title="Edit"
            >
              <MdEdit />
            </Link>
          )}
          <header className="p-3 pb-0">
            {/* TODO: Fix timezones */}
            <time
              className="block h-5 text-sm"
              dateTime={formatInTimeZone(
                new Date(post.createdAt),
                "+02:00",
                "yyyy-MM-dd",
              )}
            >
              {formatInTimeZone(
                new Date(post.createdAt),
                "+02:00",
                "dd MMM yyyy",
              )}
            </time>
            <h1 className="mb-2 text-3xl leading-[1.1] font-bold">
              {post.title}
            </h1>
          </header>

          <ScrollArea className="mr-0.5 grid pr-2.5 pl-3">
            <p className="mb-2 overflow-x-hidden leading-[1.45] text-pretty break-words whitespace-pre-line">
              {post.description && linkify(post.description)}
            </p>

            <div className="mb-3 flex flex-wrap gap-1 text-sm">
              <span className="w-full">Tags:</span>
              {post.tags.map((t) => (
                <TagComponent key={t.name} tag={t} />
              ))}
            </div>

            <div className="mt-1 flex items-start gap-2">
              <Button
                as="a"
                href={`${currentImage.urls.full}?dl=1`}
                download
                rel="noreferrer"
              >
                <MdDownload className="-ml-1" />
                Download
              </Button>

              {["available", "unreleased"].includes(post.altvStatus) && (
                <Button
                  role="link"
                  disabled={post.altvStatus === "unreleased"}
                  onClick={() =>
                    (window.location.href = `https://altv.irisarco.art/viewer?url=${encodeURIComponent(post.altv?.url || "")}`)
                  }
                >
                  <MdAutoFixHigh className="-ml-1" />
                  Customize
                </Button>
              )}
            </div>
            {post.altv && (
              <div className="mt-2 ml-0.5 text-sm">
                <A href={post.altv.url} download={post.altv.filename}>
                  Download .altv file
                </A>
              </div>
            )}

            {post.altvStatus === "unreleased" && (
              <div className="mt-2 flex gap-2 text-xs">
                <div className="mt-0.5">
                  <MdErrorOutline />
                </div>
                <div>
                  Recent image customization is for supporters only.{" "}
                  <A
                    href="https://subscribestar.adult/irisarco"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Become a subscriber
                  </A>{" "}
                  or wait for release.
                </div>
              </div>
            )}

            {post.images.length > 1 && (
              <>
                <div className="mt-2 mb-2 text-lg">Image variants:</div>
                <ul className="grid w-full grid-cols-2 gap-2">
                  {post.images.map((p, i) => (
                    <li
                      className="bg-primary-4 dark:bg-primary-3 relative aspect-square items-center overflow-hidden rounded-sm"
                      key={p.urls.full}
                    >
                      <Link
                        href={`${basePath}/${i + 1}`}
                        replace
                        className="relative grid size-full"
                      >
                        {p.urls.thumbSquare.endsWith(".mp4") ? (
                          <video
                            className="h-full w-full object-cover"
                            src={p.urls.thumbSquare}
                            autoPlay
                            muted
                            controls={false}
                            loop
                          />
                        ) : (
                          <Image
                            src={p.urls.thumbSquare}
                            alt={`Version ${i + 1}`}
                            fill
                          />
                        )}
                        {p.urls.full === currentImage.urls.full && (
                          <div className="absolute inset-0 ring-3 ring-blue-400/80 ring-inset" />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {similarPosts.length > 0 && (
              <>
                <div className="mt-2 mb-2 text-lg">You may also like:</div>
                <ul className="grid w-full grid-cols-2 gap-2">
                  {similarPosts.map((p) => (
                    <li
                      className="bg-primary-4 dark:bg-primary-3 relative aspect-square items-center overflow-hidden rounded-sm"
                      key={p.id + p.slug}
                    >
                      <Link
                        href={`/images/${p.id}/${p.slug}`}
                        className="relative grid size-full"
                      >
                        <Image
                          src={p.images[0].urls.thumbSquare}
                          alt={p.title}
                          fill
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </ScrollArea>

          <footer className="mt-3 p-3 pt-0 text-sm">
            If you like my art{" "}
            <A
              href="https://twitter.com/irisarconsfw"
              target="_blank"
              rel="noreferrer"
            >
              follow me on Twitter
            </A>
            , join my{" "}
            <A
              href="https://discord.gg/NsZPUMbvMN"
              target="_blank"
              rel="noreferrer"
            >
              Discord server
            </A>{" "}
            or{" "}
            <A
              href="https://subscribestar.adult/irisarco"
              target="_blank"
              rel="noreferrer"
            >
              support me
            </A>{" "}
            :)
          </footer>
        </Card>
      </div>
      <FullscreenImage
        basePath={basePath}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        post={post}
        currentIndex={currentIndex}
      />
    </article>
  );
};

export default ImagePage;
